import React, { useEffect, useMemo, useState } from 'react';

import ItemTagChips from './ItemTagChips';
import ItemTagPicker from './ItemTagPicker';
import TagReasons from './TagReasons';
import useCollectionTags from '../../hooks/useCollectionTags';
import useItemTagWriter from '../../hooks/useItemTagWriter';
import { rememberItemTagTitles, rememberedItemTagTitles } from '../../hooks/useTagSearch';
import { useOptionalTags } from '../../contexts/TagsContext';
import { normalizeId } from '../../utils/ids';
import { autoTagIdsOf, canRemoveEntry, handTagIdsOf, itemTagEntries, managesTagOwner } from '../../utils/itemTags';
import { tagOwnerCollectionId } from '../../utils/tags';

/**
 * A SAVED ITEM'S TAGS, editable where it is read (docs/plans/tags.md §5.2):
 * the read view's chip row and the ⋯ "Tag…" dialog. The chips
 * (ItemTagChips), then "+ Add tag" (ItemTagPicker), writing as it goes
 * (hooks/useItemTagWriter) — there is no Save: a read view has none.
 *
 * - Private tags work with READ access only: someone who can read the item
 *   but not change it still adds and removes their own private tags, and
 *   the picker greys the collection tags with the reason.
 * - A rule's application (the automation glyph) has no ✕; with a hand one
 *   too, the ✕ takes off the hand one only (the chip stays while the rule
 *   holds). Its glyph opens "Why" (TagReasons, T6a): the rule's reasons for
 *   this item, and — for managers of the tag's collection — Edit rule.
 *   A restricted tag's ✕ shows only for managers of its collection.
 *   A private tag the collection carries says "From the collection", no ✕.
 *
 * Each chip opens its tag page (T5) — a read view is where "what else has this
 * tag?" is asked — unless `linked` is false.
 *
 * The item the chips show is this editor's own copy, moved on by every write
 * and handed to `onItemUpdated` (the list's feed puts it in the row's place);
 * a new item from the parent replaces it.
 */
export default function ItemTagEditor({
	item,
	collection = null,
	collectionId = null,
	canEdit = false,
	onItemUpdated,
	pickerOpen,
	onPickerOpenChange,
	className = '',
	label = 'Tags',
	linked = true,
}) {
	const [current, setCurrent] = useState(item);
	useEffect(() => { setCurrent(item); }, [item]);
	useEffect(() => { rememberItemTagTitles(item); }, [item]);

	const itemCollectionId = normalizeId(current?.collection_id) || normalizeId(collectionId) || normalizeId(collection?._id) || null;
	// The vocabulary is asked for when it has something to add: the item
	// carries collection tags (their names come with the item's tagTitles,
	// but the vocabulary is this session's freshest word), or the picker
	// opens. Reading an untagged item asks nothing.
	const [pickerOpenState, setPickerOpenState] = useState(false);
	const pickerIsOpen = typeof pickerOpen === 'boolean' ? pickerOpen : pickerOpenState;
	const wantsVocabulary = pickerIsOpen || handTagIdsOf(current).length > 0 || autoTagIdsOf(current).length > 0;
	const { tags: vocabulary } = useCollectionTags(itemCollectionId, { enabled: wantsVocabulary });
	const tagsContext = useOptionalTags();
	const privateTags = tagsContext?.tags || null;

	const writer = useItemTagWriter({
		item: current,
		collectionId: itemCollectionId,
		onItemUpdated: next => {
			setCurrent(next);
			if (typeof onItemUpdated === 'function') onItemUpdated(next);
		},
	});

	const entries = useMemo(
		() => itemTagEntries(current, { vocabulary, privateTags, titles: rememberedItemTagTitles() }),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[current, vocabulary, privateTags, writer.busy]
	);

	const handleRemove = entry => {
		if (entry.private) writer.setPrivate({ remove: [{ tagId: entry.id }] });
		else writer.setHand({ remove: [entry.id] });
	};

	const handleToggle = ({ kind, id, on, tag, name, titles, itemTag }) => {
		if (kind === 'private') {
			const application = { tagId: id, kind: itemTag ? 'item' : 'tag', ...(name ? { name } : {}) };
			if (on) writer.setPrivate({ add: [application] });
			else writer.setPrivate({ remove: [application] });
			if (titles) rememberTitles(titles);
			return;
		}
		if (on) writer.setHand({ add: [id] }, { titles: titles || null });
		else writer.setHand({ remove: [id] });
		void tag;
	};

	const itemId = normalizeId(current?._id);
	const explainFor = itemId ? entry => () => (
		<TagReasons
			itemId={itemId}
			version={current?.updatedAt || null}
			tagId={entry.id}
			canEditRule={managesTagOwner(collection, tagOwnerCollectionId(entry.tag))}
			ruleCollectionId={tagOwnerCollectionId(entry.tag)}
		/>
	) : undefined;

	return (
		<div className={['item-tags', className].filter(Boolean).join(' ')}>
			<ItemTagChips
				entries={entries}
				label={label}
				explainFor={explainFor}
				onRemove={handleRemove}
				canRemove={entry => canRemoveEntry(entry, { canEdit, collection })}
				linked={linked}
			/>
			<ItemTagPicker
				item={current}
				collection={collection}
				canEdit={canEdit}
				vocabulary={vocabulary}
				privateTags={privateTags}
				onToggle={handleToggle}
				open={pickerIsOpen}
				onOpenChange={next => { setPickerOpenState(next); onPickerOpenChange?.(next); }}
			/>
			{writer.modals}
		</div>
	);
}

function rememberTitles(titles) {
	rememberItemTagTitles({ tagTitles: titles });
}
